import { useTranslation } from 'react-i18next';

import { GetMollieConnectionsQuery as GetMollieConnections, MollieAppPricingType } from '__generated__/graphql';
import { assetUrl } from '../../common/helpers';
import GetMollieConnectionsQuery from './GetMollieConnectionsQuery';
import MollieConnectionSection from './MollieConnectionSection';
import UI from '../../common/UI';
import config from '../../config';
import useQuery from '../../api/useQuery';

interface EditPaymentsPageProps {
  projectId: string;
}

const EditPaymentsPage = ({ projectId }: EditPaymentsPageProps) => {
  const { data } = useQuery(GetMollieConnectionsQuery, {
    variables: {
      id: projectId,
    },
  });

  const project = data?.project;

  if (!project) {
    return <UI.PageLoader />;
  }

  return <EditPaymentsView project={data.project} />;
};

const EditPaymentsView = ({ project }: GetMollieConnections) => {
  const { t } = useTranslation();

  const hasDefaultProfile = project.payment_profiles.filter((profile) => profile.default).length > 0;
  const hasUsableProfile = project.mollie_connections.filter((connection) => (
    connection.connected && connection.can_receive_payments && connection.profiles.length > 0
  )).length > 0;

  const managedFeeOrganisationIds = project.mollie_connections
    .filter((connection) => connection.mollie_app.pricing_type === MollieAppPricingType.managed_fees)
    .map((connection) => connection.organization_id);

  /**
   * Hide Mollie connections that use the old 'client pricing' Mollie app, if a
   * connection with the same organisation ID using the 'managed fees' app exists.
   */
  const visibleConnections = project.mollie_connections.filter((connection) => (
    connection.mollie_app.pricing_type === MollieAppPricingType.managed_fees
      || !managedFeeOrganisationIds.includes(connection.organization_id)
  ));

  return (
    <UI.Container sc={{ compact: true, px: [0, 4] }}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H3>
            {t('payments')}
          </UI.H3>
          <UI.InputDescription sc={{ mt: 1 }}>
            {t('mollie_description')}
          </UI.InputDescription>
        </UI.Div>

        {!hasDefaultProfile && hasUsableProfile && (
          <UI.Warning>
            {t('mollie_connected_but_no_default_profile')}
          </UI.Warning>
        )}

        {visibleConnections.map((mollieConnection) => (
          <MollieConnectionSection
            connection={mollieConnection}
            project={project}
            key={mollieConnection.id}
          />
        ))}

        {visibleConnections.length === 0 && (
          <UI.Card>
            <UI.Strong sc={{ block: true, mb: 2 }}>
              <UI.StatusIndicator sc={{ background: 'error', mr: 1 }} />
              {' '}
              {t('payments_disabled')}
            </UI.Strong>
            <UI.Paragraph>{t('mollie_required')}</UI.Paragraph>
            <UI.LogoContainer href={`${config.backendPath}/api/mollie/authorize/${project.id}`} target="_blank" rel="noopener noreferrer">
              <img src={assetUrl('images/connect-mollie.png')} alt={t('connect_with_mollie')} />
            </UI.LogoContainer>
          </UI.Card>
        )}

        {visibleConnections.length > 0 && (
          <UI.Card sc={{ background: 'gray.50' }}>
            <UI.Paragraph>{t('add_psp_connection_description')}</UI.Paragraph>
            <UI.LogoContainer href={`${config.backendPath}/api/mollie/authorize/${project.id}`} target="_blank" rel="noopener noreferrer">
              <img src={assetUrl('images/connect-mollie.png')} alt={t('connect_with_mollie')} />
            </UI.LogoContainer>
          </UI.Card>
        )}
      </UI.FormGrid>
    </UI.Container>
  );
};

export default EditPaymentsPage;
